import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { CheckCircle2, Info, X, XCircle } from "lucide-react";
import { cn } from "../lib/cn";

type ToastVariant = "info" | "success" | "error";

interface Toast {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toast: (t: { title: string; description?: string; variant?: ToastVariant; action?: { label: string; onClick: () => void } }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const icons = {
  info: Info,
  success: CheckCircle2,
  error: XCircle,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback<ToastContextValue["toast"]>(
    ({ title, description, variant = "info", action }) => {
      const id = ++counter.current;
      setToasts((prev) => [...prev, { id, title, description, variant, action }]);
      setTimeout(() => dismiss(id), 5000);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed top-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
        role="region"
        aria-label="Notifications"
      >
        {toasts.map((t) => {
          const Icon = icons[t.variant];
          return (
            <div
              key={t.id}
              role="alert"
              className={cn(
                "relative overflow-hidden pointer-events-auto flex animate-slide-down items-start gap-3 rounded-lg border bg-popover p-4 text-popover-foreground shadow-elevated",
                t.variant === "error" && "border-destructive/40",
                t.variant === "success" && "border-success/40",
              )}
            >
              <Icon
                className={cn(
                  "mt-0.5 h-5 w-5 shrink-0",
                  t.variant === "error" && "text-destructive",
                  t.variant === "success" && "text-success",
                  t.variant === "info" && "text-primary",
                )}
              />
              <div className="flex-1 space-y-0.5">
                <p className="text-sm font-medium">{t.title}</p>
                {t.description && (
                  <p className="text-sm text-muted-foreground">{t.description}</p>
                )}
              </div>
              {t.action && (
                <button
                  onClick={() => {
                    t.action?.onClick();
                    dismiss(t.id);
                  }}
                  className="shrink-0 rounded border border-border px-2 py-1 text-xs font-medium hover:bg-muted transition-colors"
                >
                  {t.action.label}
                </button>
              )}
              <button
                onClick={() => dismiss(t.id)}
                className="text-muted-foreground transition-colors hover:text-foreground shrink-0 mt-0.5"
                aria-label="Dismiss notification"
              >
                <X className="h-4 w-4" />
              </button>
              <div 
                className={cn(
                  "absolute bottom-0 left-0 h-1 bg-primary origin-left animate-[progress-fill_5s_linear_forwards]",
                  t.variant === "error" && "bg-destructive",
                  t.variant === "success" && "bg-success"
                )} 
                style={{ width: "100%" }}
              />
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}
